import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MapPin, BadgeCheck, Mic } from "lucide-react";
import Tilt3D from "@/components/anim/Tilt3D";
import { ChatGPTIcon } from "@/data/platforms";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

const CASES_EN = [
  {
    query: "best barbershop near me",
    aiLine: "Here’s the top-rated barbershop near you — customers love it:",
    name: "Fade District Barbershop",
    img: "https://static.prod-images.emergentagent.com/jobs/fdc7e537-5c5d-45e2-9d51-2eada049b3ca/images/20351fd1a7f787a5e4d896e47c6f4c2b335f7070d3073763254146c5849955b4.png",
    rating: "4.9",
    reviews: "1,248",
    meta: "212 Melrose Ave · Open until 8 PM",
    cta: "Book Appointment →",
  },
  {
    query: "best dentist near me",
    aiLine: "The highest-rated dental clinic near you is:",
    name: "Brightline Dental Studio",
    img: "https://static.prod-images.emergentagent.com/jobs/fdc7e537-5c5d-45e2-9d51-2eada049b3ca/images/256dc980c813b0b33016051cd6fd08d6787eb864ccee6064908ff31183462660.png",
    rating: "4.9",
    reviews: "932",
    meta: "45 Harbor Blvd · Accepting new patients",
    cta: "Book a Visit →",
  },
  {
    query: "best italian restaurant near me",
    aiLine: "Locals consistently recommend this spot:",
    name: "Nonna’s Table",
    img: "https://static.prod-images.emergentagent.com/jobs/fdc7e537-5c5d-45e2-9d51-2eada049b3ca/images/fda0252e255d7af0ae8b31e36f8ecddb6129f3c859d18e5322ef915b3f1a6819.png",
    rating: "4.8",
    reviews: "2,103",
    meta: "78 Via Roma St · Tables available tonight",
    cta: "Reserve a Table →",
  },
  {
    query: "top real estate agent near me",
    aiLine: "This agency has the strongest track record in your area:",
    name: "Hartman & Co. Realty",
    img: "https://static.prod-images.emergentagent.com/jobs/fdc7e537-5c5d-45e2-9d51-2eada049b3ca/images/460f781b2f111b45294c89ee7cb897cdd41875ea160a88e33f4f809d52145fb5.png",
    rating: "5.0",
    reviews: "487",
    meta: "310 Skyline Dr · Free consultation",
    cta: "Contact Agent →",
  },
];

const CASES_HE = [
  {
    query: "אינסטלטור מומלץ בחולון",
    aiLine: "האינסטלטור המדורג הכי גבוה בחולון:",
    name: "אבי כהן אינסטלציה",
    img: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=400",
    rating: "4.9",
    reviews: "487",
    meta: "זמין היום · חולון והסביבה",
    cta: "לתיאום הגעה ←",
  },
  {
    query: "חשמלאי מוסמך בראשון לציון",
    aiLine: "החשמלאי המומלץ ביותר בראשון לציון:",
    name: "דני חשמל ושירות",
    img: "https://images.pexels.com/photos/8005397/pexels-photo-8005397.jpeg?auto=compress&cs=tinysrgb&w=400",
    rating: "4.8",
    reviews: "312",
    meta: "מענה 24/6 · ראשון לציון",
    cta: "להזמנת טכנאי ←",
  },
  {
    query: "מספרה שווה ברמת גן",
    aiLine: "המספרה המדורגת הכי גבוה ברמת גן:",
    name: "סטודיו מאיה",
    img: "https://static.prod-images.emergentagent.com/jobs/fdc7e537-5c5d-45e2-9d51-2eada049b3ca/images/20351fd1a7f787a5e4d896e47c6f4c2b335f7070d3073763254146c5849955b4.png",
    rating: "4.9",
    reviews: "289",
    meta: "יש תור פנוי מחר · רמת גן",
    cta: "לקביעת תור ←",
  },
];

const CASES = HE ? CASES_HE : CASES_EN;

const goScan = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  setTimeout(() => document.querySelector('[data-testid="domain-input"]')?.focus({ preventScroll: true }), 700);
};

const TypingDots = () => (
  <div className="flex items-center gap-1 bg-white border border-neutral-200 rounded-2xl rounded-bl-sm px-4 py-3 w-fit">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-2 w-2 rounded-full bg-neutral-400"
        animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
        transition={{ repeat: Infinity, duration: 0.9, delay: i * 0.15 }}
      />
    ))}
  </div>
);

const ResultCard = ({ c }) => (
  <motion.div
    initial={{ opacity: 0, y: 24, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ type: "spring", stiffness: 260, damping: 22 }}
    onClick={goScan}
    className="bg-white border-2 border-obsidian overflow-hidden shadow-[5px_5px_0px_0px_rgba(10,10,10,1)] cursor-pointer"
    data-testid="demo-ai-card"
  >
    {c.img ? (
      <div className="relative">
        <img src={c.img} alt={c.name} className="h-28 sm:h-32 w-full object-cover" />
        <span className="absolute top-2 left-2 bg-conversion text-white font-data text-[9px] font-bold uppercase tracking-widest px-2 py-1">
          {t("demo.badge")}
        </span>
      </div>
    ) : (
      <div className="px-3 pt-3">
        <span className="inline-block bg-conversion text-white font-data text-[9px] font-bold uppercase tracking-widest px-2 py-1">
          {t("demo.badge")}
        </span>
      </div>
    )}
    <div className="p-3">
      <div className="flex items-center gap-1.5">
        <span className="font-heading font-bold text-sm text-obsidian">{c.name}</span>
        <BadgeCheck className="h-4 w-4 text-[#4285F4] shrink-0" />
      </div>
      <div className="flex items-center gap-1.5 mt-1">
        <div className="flex">
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.span key={i} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25 + i * 0.09 }}>
              <Star className="h-3.5 w-3.5 fill-[#FFB300] text-[#FFB300]" />
            </motion.span>
          ))}
        </div>
        <span className="font-data text-xs font-bold text-obsidian" dir="ltr">{c.rating}</span>
        <span className="font-data text-[10px] text-neutral-500">(<span dir="ltr">{c.reviews}</span> {t("demo.reviews")})</span>
      </div>
      <div className="flex items-center gap-1 mt-1.5 text-neutral-600">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        <span className="text-xs">{c.meta}</span>
      </div>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-2.5 w-full bg-obsidian text-white font-data text-[11px] font-bold uppercase tracking-widest py-2.5"
        tabIndex={-1}
        type="button"
        onClick={goScan}
      >
        {c.cta}
      </motion.button>
    </div>
  </motion.div>
);

const ChatBody = ({ step, typed, caseIdx, c }) => (
  <div className="bg-[#F4F4F5] px-3.5 pt-4 pb-3 h-[380px] sm:h-[420px] flex flex-col gap-3 overflow-hidden">
    <AnimatePresence>
      {step >= 1 && (
        <motion.div
          key={`user-${caseIdx}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="self-end bg-obsidian text-white rounded-2xl rounded-br-sm px-4 py-3 max-w-[85%]"
          data-testid="demo-user-query"
        >
          <span className="text-sm">{typed}</span>
          {step === 1 && <motion.span className="inline-block w-[2px] h-4 bg-white ml-0.5 align-middle" animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.7 }} />}
        </motion.div>
      )}
      {step === 2 && (
        <motion.div key={`dots-${caseIdx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="self-start">
          <TypingDots />
        </motion.div>
      )}
      {step >= 3 && (
        <motion.div
          key={`ai-text-${caseIdx}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="self-start bg-white border border-neutral-200 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[90%]"
        >
          <span className="text-sm text-obsidian">{c.aiLine}</span>
        </motion.div>
      )}
      {step >= 4 && (
        <motion.div key={`card-${caseIdx}`} exit={{ opacity: 0 }} className="self-start w-full max-w-[92%]">
          <ResultCard c={c} />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const LiveDemoChat = () => {
  const [step, setStep] = useState(0); // 0 idle, 1 typing, 2 ai-thinking, 3 ai-text, 4 card
  const [typed, setTyped] = useState("");
  const [caseIdx, setCaseIdx] = useState(0);
  const c = CASES[caseIdx];

  useEffect(() => {
    let timers = [];
    let interval;
    const run = (idx) => {
      setCaseIdx(idx);
      setTyped("");
      setStep(1);
      const q = CASES[idx].query;
      let i = 0;
      interval = setInterval(() => {
        i++;
        setTyped(q.slice(0, i));
        if (i >= q.length) {
          clearInterval(interval);
          timers.push(setTimeout(() => setStep(2), 500));
          timers.push(setTimeout(() => setStep(3), 1800));
          timers.push(setTimeout(() => setStep(4), 2600));
          timers.push(
            setTimeout(() => {
              setStep(0);
              timers.push(setTimeout(() => run((idx + 1) % CASES.length), 800));
            }, 8600)
          );
        }
      }, 55);
    };
    timers.push(setTimeout(() => run(0), 800));
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, []);

  return (
    <div data-testid="live-demo-chat">
      <Tilt3D max={8} className="relative">
        <div
          className="relative mx-auto w-full max-w-[370px] rounded-[2rem] border-[5px] border-obsidian bg-obsidian overflow-hidden shadow-[14px_14px_0px_0px_rgba(255,69,0,1)]"
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* chat header */}
          <div className="flex items-center gap-2.5 bg-obsidian px-4 py-3">
            <div className="bg-white rounded-full p-1.5">
              <ChatGPTIcon style={{ fontSize: 18, color: "#0A0A0A" }} />
            </div>
            <div>
              <div className="font-heading font-bold text-sm text-white leading-none" dir="ltr">ChatGPT</div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-data-positive animate-pulse" />
                <span className="font-data text-[9px] uppercase tracking-widest text-neutral-400">{t("demo.answering")}</span>
              </div>
            </div>
          </div>

          {/* chat body */}
          <ChatBody step={step} typed={typed} caseIdx={caseIdx} c={c} />

          {/* fake input */}
          <div className="bg-[#F4F4F5] px-3.5 pb-4">
            <div className="flex items-center gap-2 bg-white border border-neutral-300 rounded-full px-4 py-2.5">
              <span className="text-sm text-neutral-400 flex-1">{t("demo.ask")}</span>
              <Mic className="h-4 w-4 text-neutral-400" />
            </div>
          </div>
        </div>
      </Tilt3D>

      {/* case indicator dots */}
      <div className="flex justify-center gap-1.5 mt-4" aria-hidden>
        {CASES.map((cs, i) => (
          <span key={cs.query} className={`h-1.5 transition-all duration-300 ${i === caseIdx ? "w-6 bg-conversion" : "w-1.5 bg-neutral-300"}`} />
        ))}
      </div>

      {HE ? (
        <p className="text-center mt-3 text-sm text-neutral-600 max-w-[370px] mx-auto leading-snug">
          <span className="font-bold text-obsidian">אינסטלטורים. חשמלאים. מספרות. עסקים מקומיים.</span>{" "}
          ככה לקוחות בוחרים היום — ה-AI ממליץ עליכם, או על המתחרה?
        </p>
      ) : (
        <p className="text-center mt-3 text-sm text-neutral-600 max-w-[370px] mx-auto leading-snug">
          <span className="font-bold text-obsidian">Barbers. Dentists. Restaurants. Realtors.</span>{" "}
          This is how customers choose now — is AI recommending you, or your competitor?
        </p>
      )}
    </div>
  );
};

export default LiveDemoChat;
